import { useEffect, useMemo, useState } from 'react'
import supabase from '@/supabaseClient'

type IGDBGameMedia = {
  id: number
  name: string
  cover?: { image_id?: string }
  artworks?: { image_id?: string }[]
  screenshots?: { image_id?: string }[]
}

const IGDB_PROXY_URL = import.meta.env.VITE_API_URL + '/api/igdb'

const coverSizes = ['t_cover_small', 't_cover_big', 't_720p', 't_1080p', 't_original']
const artworkSizes = ['t_artwork_thumb', 't_artwork_big', 't_720p', 't_1080p', 't_original']
const screenshotSizes = ['t_screenshot_med', 't_screenshot_big', 't_screenshot_huge', 't_1080p', 't_original']

const buildImageUrl = (imageId: string, size: string) =>
  `https://images.igdb.com/igdb/image/upload/${size}/${imageId}.jpg`

const IGDBImageExplorer = () => {
  const [gameId, setGameId] = useState('236664')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [game, setGame] = useState<IGDBGameMedia | null>(null)

  const fetchGame = async (id: string) => {
    if (!id.trim()) return
    setLoading(true)
    setError(null)
    try {
      const query = `
        fields name, cover.image_id, artworks.image_id, screenshots.image_id;
        where id = ${id};
      `
      const { data: { session } } = await supabase.auth.getSession()
      const response = await fetch(`${IGDB_PROXY_URL}/games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ query }),
      })

      if (!response.ok) {
        throw new Error(`IGDB request failed: ${response.statusText}`)
      }

      const data = (await response.json()) as IGDBGameMedia[]
      setGame(data[0] || null)
      if (!data[0]) {
        setError('No game found for that ID.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load IGDB data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchGame(gameId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const coverId = game?.cover?.image_id
  const artworkIds = useMemo(
    () => game?.artworks?.map((art) => art.image_id).filter(Boolean) as string[] || [],
    [game?.artworks]
  )
  const screenshotIds = useMemo(
    () => game?.screenshots?.map((shot) => shot.image_id).filter(Boolean) as string[] || [],
    [game?.screenshots]
  )

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-2">IGDB Image Explorer</h1>
      <p className="text-sm text-base-content/70 mb-6">
        Enter an IGDB game ID to inspect available cover, artwork, and screenshot images.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end mb-6">
        <label className="form-control w-full sm:max-w-xs">
          <span className="label-text">IGDB Game ID</span>
          <input
            type="text"
            value={gameId}
            onChange={(event) => setGameId(event.target.value)}
            className="input input-bordered"
            placeholder="236664"
          />
        </label>
        <button className="btn btn-primary" onClick={() => fetchGame(gameId)} disabled={loading}>
          {loading ? 'Loading...' : 'Load Images'}
        </button>
      </div>

      {error && <div className="text-sm text-error mb-6">{error}</div>}

      {game && (
        <div className="space-y-8">
          <div className="text-lg font-semibold">{game.name}</div>

          <section>
            <h2 className="text-base font-semibold mb-3">Cover</h2>
            {coverId ? (
              <div className="bg-base-100 border rounded-lg p-4">
                <img
                  src={buildImageUrl(coverId, 't_cover_big')}
                  alt="Cover preview"
                  className="w-56 rounded shadow"
                />
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {coverSizes.map((size) => (
                    <a
                      key={size}
                      className="link link-primary"
                      href={buildImageUrl(coverId, size)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {size}
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-base-content/70">No cover image found.</p>
            )}
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">Artworks</h2>
            {artworkIds.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {artworkIds.map((id) => (
                  <div key={id} className="bg-base-100 border rounded-lg p-4">
                    <img
                      src={buildImageUrl(id, 't_720p')}
                      alt="Artwork preview"
                      className="w-full rounded shadow"
                    />
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {artworkSizes.map((size) => (
                        <a
                          key={size}
                          className="link link-primary"
                          href={buildImageUrl(id, size)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {size}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-base-content/70">No artwork images found.</p>
            )}
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">Screenshots</h2>
            {screenshotIds.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {screenshotIds.map((id) => (
                  <div key={id} className="bg-base-100 border rounded-lg p-4">
                    <img
                      src={buildImageUrl(id, 't_screenshot_big')}
                      alt="Screenshot preview"
                      className="w-full rounded shadow"
                    />
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {screenshotSizes.map((size) => (
                        <a
                          key={size}
                          className="link link-primary"
                          href={buildImageUrl(id, size)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {size}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-base-content/70">No screenshots found.</p>
            )}
          </section>
        </div>
      )}
    </div>
  )
}

export default IGDBImageExplorer
